import { createFileRoute } from "@tanstack/react-router"
import { type FormEvent, type ReactNode, useState } from "react"
import { ContactRow } from "@/components/ui/contact-row"
import { PageVignette } from "@/components/ui/page-vignette"
import { SectionHeader } from "@/components/ui/section-header"
import { SectionTitle } from "@/components/ui/section-title"
import { SharedButton } from "@/components/ui/shared-button"
import { StarField } from "@/components/ui/star-field"

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact | EKLO-Verse" },
      {
        name: "description",
        content:
          "Reach the EKLO-Verse team — production, press, and partnership inquiries.",
      },
      { property: "og:title", content: "Contact | EKLO-Verse" },
      {
        property: "og:description",
        content: "Reach the EKLO-Verse team.",
      },
      { property: "og:url", content: "https://eklo-seven.vercel.app/contact" },
    ],
    links: [
      { rel: "canonical", href: "https://eklo-seven.vercel.app/contact" },
    ],
  }),
  component: ContactPage,
})

function ContactPage() {
  return (
    <div className="fade-in relative animate-in overflow-x-hidden px-4 pt-24 pb-24 duration-500 sm:px-8 sm:pt-30 sm:pb-30">
      <StarField />
      <PageVignette />
      <div className="mx-auto max-w-320">
        <div className="flex justify-center">
          <SectionTitle
            prefix="Get in"
            accent="touch."
            className="mb-8 justify-center"
          />
        </div>

        <div className="mx-auto mb-20 flex w-full max-w-none flex-col gap-4 text-center font-mono text-foreground text-sm leading-relaxed sm:mb-24 sm:text-base sm:leading-[1.7] lg:max-w-[80ch]">
          <p>
            SHY Productions Ltd. is a UK/Nepal based film production company
            dedicated to world-building, genre cinema, and stories rooted in the
            Himalayas. We create bold, immersive narratives that bridge local
            authenticity with global appeal.
          </p>
          <p>
            For distribution, rights acquisition, onboarding opportunities, or
            collaboration on future installments, please reach out to us below.
          </p>
        </div>

        <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-2 lg:gap-20">
          <div className="lg:order-2">
            <ContactForm />
          </div>
          <div className="lg:order-1">
            <ContactInfo />
          </div>
        </div>
      </div>
    </div>
  )
}

function ContactInfo() {
  return (
    <div>
      <img
        src="https://70fy8xbp6b.ufs.sh/f/BzM3qquY5hLZiF1TDkbdvV6aHBk1WmSs9T8IPn2xYy0ezhjf"
        alt="SHY Productions"
        width={600}
        height={600}
        className="mb-10 h-40 w-auto object-contain sm:h-48"
        loading="lazy"
        decoding="async"
      />
      <div className="mb-10 flex flex-col gap-8">
        <ContactRow label="Studio · SHY Productions Ltd">
          <ContactLink href="mailto:contact@shyproductions.co.uk">
            contact@shyproductions.co.uk
          </ContactLink>
        </ContactRow>
        <ContactRow label="Director · Pradeep Shahi">
          <ContactLink href="https://pradeepshahi.com">
            pradeepshahi.com
          </ContactLink>
        </ContactRow>
        <ContactRow label="Phone · UK">
          <ContactLink href="tel:+447517927542">+44 7517 927542</ContactLink>
        </ContactRow>
        <ContactRow label="Studios">
          <span className="font-mono text-foreground text-sm">
            London &nbsp;↔&nbsp; Nepal
          </span>
        </ContactRow>
      </div>

      <blockquote className="max-w-[56ch] border-brand border-l-2 py-2 pl-6 font-mono text-base text-foreground leading-relaxed">
        "Send us a message. We read every one and reply when we can."
        <cite className="mt-3.5 block font-mono font-semibold text-[10px] text-muted-foreground uppercase not-italic tracking-[0.22em]">
          — Mutu_01 · auto-reply
        </cite>
      </blockquote>
    </div>
  )
}

function ContactLink({
  href,
  children,
}: {
  href: string
  children: ReactNode
}) {
  const external = /^https?:/.test(href)
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      className="self-start font-mono text-foreground text-sm underline decoration-white/25 underline-offset-4 transition-colors hover:text-brand hover:decoration-brand"
    >
      {children}
    </a>
  )
}

function ContactForm() {
  const [sent, setSent] = useState(false)

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setSent(true)
  }

  return (
    <form
      onSubmit={onSubmit}
      className="flex flex-col gap-5 border border-white/15 bg-[#07101f]/55 p-5 backdrop-blur-sm sm:p-7 lg:p-8"
    >
      <SectionHeader num="FORM·01" label="Enquiry" />

      <Field id="cf-name" label="Name">
        <input
          id="cf-name"
          name="name"
          type="text"
          required
          placeholder="Your name"
          className={inputCls}
        />
      </Field>

      <Field id="cf-email" label="Email">
        <input
          id="cf-email"
          name="email"
          type="email"
          required
          placeholder="you@studio.com"
          className={inputCls}
        />
      </Field>

      <Field id="cf-type" label="Enquiry type">
        <select
          id="cf-type"
          name="type"
          defaultValue="general"
          className={`${inputCls} cursor-pointer appearance-none bg-[length:6px_6px,6px_6px] bg-[position:calc(100%-18px)_50%,calc(100%-12px)_50%] bg-no-repeat pr-8 [background-image:linear-gradient(45deg,transparent_50%,#a8c0e8_50%),linear-gradient(135deg,#a8c0e8_50%,transparent_50%)]`}
        >
          <option value="general">General enquiry</option>
          <option value="press">Press &amp; media</option>
          <option value="distribution">Distribution</option>
          <option value="festival">Festival programming</option>
          <option value="collab">Collaboration</option>
          <option value="other">Other</option>
        </select>
      </Field>

      <Field id="cf-msg" label="Message">
        <textarea
          id="cf-msg"
          name="message"
          required
          placeholder="What is the transmission about?"
          className={`${inputCls} min-h-[130px] resize-y leading-[1.55]`}
        />
      </Field>

      <SharedButton type="submit" className="mt-1.5 self-start">
        {sent ? "Transmission sent" : "Send transmission"}
      </SharedButton>
    </form>
  )
}

const inputCls =
  "border border-white/15 bg-[#030611]/55 px-3.5 py-3 font-mono text-sm text-foreground outline-none transition-colors focus:border-brand focus:bg-[#030611]/75"

function Field({
  id,
  label,
  children,
}: {
  id: string
  label: string
  children: ReactNode
}) {
  return (
    <div className="flex flex-col gap-2">
      <label
        htmlFor={id}
        className="font-mono font-semibold text-[10px] text-muted-foreground uppercase tracking-[0.22em]"
      >
        {label}
      </label>
      {children}
    </div>
  )
}
